import React, { useMemo, useState } from 'react';
import { Button, Group, List, Modal, Select, Stack, Text, Textarea } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
    PRESCRIPTION_UNLOCK_REASONS,
    Prescription,
    PrescriptionUnlockReason,
} from '../../../../global/entities/Prescription/Prescription';

interface UnlockModalProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: (reasonCode: PrescriptionUnlockReason, note: string | null) => void;
    prescriptions: Prescription[];
    unlockingHcpName: string;
    isLoading?: boolean;
}

export const UnlockModal: React.FC<UnlockModalProps> = ({
    opened,
    onClose,
    onConfirm,
    prescriptions,
    unlockingHcpName,
    isLoading,
}) => {
    const { t, i18n } = useTranslation();
    const [reasonCode, setReasonCode] = useState<PrescriptionUnlockReason | null>(null);
    const [note, setNote] = useState<string>('');
    const [touched, setTouched] = useState(false);

    const reasonOptions = useMemo(
        () =>
            PRESCRIPTION_UNLOCK_REASONS.map((code) => ({
                value: String(code),
                label: t(`prescriptionHistory.unlock.reason.${PrescriptionUnlockReason[code]}`),
            })),
        [t],
    );

    const noteRequired = reasonCode === PrescriptionUnlockReason.Other;
    const noteInvalid = noteRequired && note.trim().length === 0;
    const canSubmit = reasonCode !== null && !noteInvalid;

    const formatDate = (d: Date | null | undefined) => {
        if (!d) return '';
        return new Date(d).toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    };

    const handleConfirm = () => {
        setTouched(true);
        if (!canSubmit) return;
        onConfirm(reasonCode!, note.trim() === '' ? null : note.trim());
    };

    const handleClose = () => {
        setReasonCode(null);
        setNote('');
        setTouched(false);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={t('prescriptionHistory.unlock.modalTitle')}
            size="md"
            centered
        >
            <Stack gap="sm">
                <Text size="sm">
                    {t('prescriptionHistory.unlock.affecting', { count: prescriptions.length })}
                </Text>
                <List size="sm" withPadding>
                    {prescriptions.map((p) => (
                        <List.Item key={p.id}>
                            <Text span fw={500}>
                                {p.drugName}
                            </Text>
                            {p.validatedByName && (
                                <Text span size="xs" c="dimmed">
                                    {' — '}
                                    {t('prescriptionHistory.unlock.originalValidator', {
                                        name: p.validatedByName,
                                        timestamp: formatDate(p.validatedAtUtc),
                                    })}
                                </Text>
                            )}
                        </List.Item>
                    ))}
                </List>

                <Select
                    label={t('prescriptionHistory.unlock.reason.label')}
                    placeholder={t('prescriptionHistory.unlock.reason.placeholder')}
                    data={reasonOptions}
                    value={reasonCode !== null ? String(reasonCode) : null}
                    onChange={(value) => setReasonCode(value ? (Number(value) as PrescriptionUnlockReason) : null)}
                    required
                    error={touched && reasonCode === null ? t('prescriptionHistory.unlock.reason.requiredError') : undefined}
                />

                <Textarea
                    label={t('prescriptionHistory.unlock.note.label')}
                    description={noteRequired ? t('prescriptionHistory.unlock.note.requiredWhenOther') : undefined}
                    placeholder={t('prescriptionHistory.unlock.note.placeholder')}
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                    minRows={2}
                    autosize
                    required={noteRequired}
                    error={touched && noteInvalid ? t('prescriptionHistory.unlock.note.requiredWhenOther') : undefined}
                />

                <Text size="xs" c="dimmed">
                    {t('prescriptionHistory.unlock.unlockerLabel', { name: unlockingHcpName })}
                </Text>

                <Group justify="flex-end" mt="md">
                    <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                        {t('button.cancel')}
                    </Button>
                    <Button color="orange" onClick={handleConfirm} loading={isLoading} disabled={!canSubmit}>
                        {t('prescriptionHistory.unlock.confirmCta')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
